import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Presentation,
  Trash2,
  ArrowLeft,
  ArrowRight,
  MapPin,
  Calendar,
  Type,
  PlusCircle,
  SkipForward,
} from "lucide-react";

export type Step6SeminarRow = {
  title: string;
  date: string;
  location?: string;
};

export default function Step6Seminars({
  onSubmit,
  busy,
  onBack,
  initialValues,
  readOnly,
}: {
  busy: boolean;
  onBack: () => void;
  readOnly?: boolean;
  initialValues?: { seminars: Step6SeminarRow[] } | null;
  onSubmit: (seminars: Step6SeminarRow[]) => Promise<void>;
}) {
  const emptyRow: Step6SeminarRow = useMemo(
    () => ({ title: "", date: "", location: "" }),
    []
  );
  const [rows, setRows] = useState<Step6SeminarRow[]>([emptyRow]);

  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!initialValues || hydratedRef.current) return;
    const incoming = initialValues.seminars || [];
    if (incoming.length === 0) return;

    hydratedRef.current = true;
    setRows(
      incoming.map((s) => ({
        title: s.title ?? "",
        date: s.date ?? "",
        location: s.location ?? "",
      }))
    );
  }, [initialValues]);

  const setRow = (i: number, k: keyof Step6SeminarRow, v: string) => {
    setRows((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };

  const addRow = () => setRows((s) => [...s, { ...emptyRow }]);

  return (
    <form
      className="space-y-8 pb-10"
      onSubmit={async (e) => {
        e.preventDefault();
        await onSubmit(rows);
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <Presentation className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Seminars & Workshops
            </h2>
            <p className="text-sm text-gray-500">
              Record professional training, conferences, or seminars attended.{" "}
              <span className="text-gray-400">(Optional — you may skip this step)</span>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {rows.map((r, i) => (
          <div
            key={i}
            className="group relative bg-white border border-gray-200 rounded-2xl p-6 transition-all hover:border-gray-300 hover:shadow-sm"
          >
            {/* Delete Button */}
            {rows.length > 1 && (
              <button
                type="button"
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                onClick={() => setRows((x) => x.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}

            <div className="grid md:grid-cols-3 gap-6">
              <Field
                label="Seminar Title"
                icon={<Type className="w-3 h-3" />}
                value={r.title}
                placeholder="e.g. Annual Accounting Summit"
                onChange={(v: string) => setRow(i, "title", v)}
              />
              <Field
                label="Date Attended"
                icon={<Calendar className="w-3 h-3" />}
                type="date"
                value={r.date}
                onChange={(v: string) => setRow(i, "date", v)}
              />
              <Field
                label="Location"
                icon={<MapPin className="w-3 h-3" />}
                value={r.location || ""}
                placeholder="e.g. Lagos, Nigeria"
                onChange={(v: string) => setRow(i, "location", v)}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all"
      >
        <PlusCircle className="w-4 h-4" />
        Add Seminar Entry
      </button>

      {!readOnly && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
          <button
            type="button"
            disabled={busy}
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-50 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={async () => onSubmit([])}
              className="flex items-center gap-2 px-6 py-3 border border-dashed border-gray-300 rounded-xl font-bold text-sm text-gray-400 hover:border-gray-500 hover:text-gray-600 transition-all"
            >
              <SkipForward className="w-4 h-4" />
              Skip
            </button>

            <button
              disabled={busy}
              className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200"
            >
              {busy ? "Processing..." : "Save & Continue"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

/* ---------- Sub-components with Explicit Typing ---------- */

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  icon?: React.ReactNode;
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  icon,
}: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
        {icon} {label}
      </label>
      <input
        type={type}
        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:bg-white focus:border-gray-400 transition-all"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
